import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Instagram } from "lucide-react";
import Logo from "@/components/Logo";

export const Footer = () => {
  const navigate = useNavigate();
  return (
    <footer id="contact" className="bg-sa-bg pb-12 pt-16" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-10 border-t border-sa-beige pt-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo iconClass="h-10 w-10" textClass="text-2xl" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-sa-muted">
              India's first precision liver health platform. Physician-led,
              evidence-based, proactive organ medicine.
            </p>
            <button
              onClick={() => navigate("/assessment")}
              className="mt-6 rounded-full bg-sa-copper px-6 py-3 text-sm font-medium text-white transition-transform duration-300 hover:scale-[1.03]"
              data-testid="footer-start-assessment"
            >
              Start Free Assessment
            </button>
          </div>

          <div>
            <p className="text-[12px] font-semibold uppercase tracking-widest text-sa-forest">Explore</p>
            <ul className="mt-4 space-y-2.5 text-sm text-sa-muted">
              <li><a href="#why" className="transition-colors hover:text-sa-forest">Why Liver Health</a></li>
              <li><a href="#risk-categories" className="transition-colors hover:text-sa-forest">Risk Categories</a></li>
              <li><a href="#roadmap" className="transition-colors hover:text-sa-forest">Recovery Program</a></li>
            </ul>
          </div>

          <div>
            <p className="text-[12px] font-semibold uppercase tracking-widest text-sa-forest">Contact</p>
            <ul className="mt-4 space-y-3 text-sm text-sa-muted">
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 flex-none text-sa-copper" />
                <a href="tel:+917861917281" className="transition-colors hover:text-sa-forest">
                  +91 78619 17281
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 flex-none text-sa-copper" />
                <a href="mailto:contact@superagehealth.com" className="transition-colors hover:text-sa-forest break-all">
                  contact@superagehealth.com
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-sa-copper" />
                <a
                  href="https://maps.google.com/?q=201,+City+Center+2,+Science+City+Rd,+Sola,+Ahmedabad,+Gujarat+380060"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="leading-relaxed transition-colors hover:text-sa-forest"
                  data-testid="footer-address"
                >
                  201, City Center 2, Science City Rd,<br />
                  Sola, Ahmedabad, Gujarat 380060, India
                </a>
              </li>
            </ul>

            <div className="mt-5 flex items-center gap-2.5" data-testid="footer-socials">
              {[
                { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/superagehealth" },
                { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/the-side-clinics/" },
              ].map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="group flex h-9 w-9 items-center justify-center rounded-full border border-sa-beige bg-white text-sa-forest/70 transition-[transform,border-color,background-color,color] duration-300 hover:-translate-y-0.5 hover:border-sa-copper hover:bg-sa-copper hover:text-white"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-sa-beige pt-6 text-xs text-sa-muted sm:flex-row sm:items-center">
          <div>
            <p>© {new Date().getFullYear()} SuperAge Health™. All rights reserved.</p>
            <p className="mt-1 italic text-sa-muted/80" data-testid="footer-parent">
              Powered by Comprehensive Organ Transplant Care Pvt. Ltd.
            </p>
          </div>
          <p className="max-w-md sm:text-right">
            The online assessment is educational and not a medical diagnosis.
            Consult a physician for clinical decisions.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
